/**
 * Legacy / unmodeled events — bulk-registered so the typed
 * `useStatsigClient.logEvent`/`useLogEvent` paths can require
 * `RegisteredEventName` everywhere without breaking pre-existing
 * fire sites. Schemas are intentionally permissive (passthrough
 * metadata, optional value) — they accept-without-validating so the
 * compile-time gate works today without forcing schema design on
 * every legacy event.
 *
 * **Don't add new events here.** Each entry is a candidate for one
 * of three follow-up actions, tracked separately:
 *
 *  1. Fold into an existing registered event with a `value` discriminator
 *     — e.g. `home_dismissed` → `ui_dismissed` with `value="home"`,
 *     `learn_more_clicked` → `cta_clicked` with `value="learn_more"`.
 *  2. Promote to a properly-modeled event in its own file with a
 *     real Zod schema (e.g. `form_submitted` deserves shape).
 *  3. Delete if dead — a few of these may have no consumers.
 *
 * The breaking-change CI test will keep these stable until the
 * cleanup ticket lands; removing or restructuring them requires the
 * usual `event_version` bump or `@deprecated` window.
 */
import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

// Permissive value: accept anything string-like or numeric. Optional.
const legacyValueSchema = z.union([z.string(), z.number()]).optional();

// Permissive metadata: accept any shape, just enforce the envelope is
// merge-compatible. `passthrough()` means unknown keys pass without
// being stripped.
const legacyMetadataSchema = baseEnvelopeSchema.passthrough();

const define = (name: string) =>
  defineEvent({
    eventName: name,
    eventVersion: 1,
    valueSchema: legacyValueSchema,
    metadataSchema: legacyMetadataSchema,
  });

// Should fold into existing events (cta_clicked / ui_dismissed / error_shown / entrypoint_viewed)
export const buttonPressed = define("button_pressed");
export const learnMoreClicked = define("learn_more_clicked");
export const startTrialClicked = define("start_trial_clicked");
export const dismiss = define("dismiss");
export const homeDismissed = define("home_dismissed");
export const infoCardDismissed = define("info_card_dismissed");
export const subscriptionFlowAbandoned = define("subscription_flow_abandoned");
export const homeEmptyStateViewed = define("home_empty_state_viewed");
export const confirmVisitRequestError = define("confirm_visit_request_error");

// Typo of the registered `billing_cadence_switched` — kept registered
// so existing fires don't break compile; cleanup ticket should rename
// the call sites.
export const billingCycleSwitched = define("billing_cycle_switched");

// Probably warrant proper schemas
export const formSubmitted = define("form_submitted");
export const formAbandoned = define("form_abandoned");
export const expressCheckoutConfirmed = define("express_checkout_confirmed");
export const expressCheckoutPressed = define("express_checkout_pressed");
export const conversionDrawerOpened = define("conversion_drawer_opened");
export const rescuedToRx = define("rescued_to_rx");
export const menuOpened = define("menu_opened");

// Paywall ANALYTICS_EVENTS values from
// apps/platform/src/app/paywall/two-prod/constants.ts. Cleanup target: fold
// into `cta_clicked` + `ui_dismissed` with discriminator values.
export const paywallDismissed = define("paywall_dismissed");
export const paywallStartFreeTrial = define("paywall_start_free_trial");
export const paywallTwoProductContinue = define("paywall_two_product_continue");

// Subscription-gate ANALYTICS_EVENTS values from
// packages/stripe/src/subscription-gate/constants.ts.
export const subscriptionGateOpened = define("subscription_gate_opened");
export const subscriptionGateDismissed = define("subscription_gate_dismissed");
export const subscriptionGateProductSelected = define(
  "subscription_gate_product_selected"
);
export const subscriptionGatePaymentStarted = define(
  "subscription_gate_payment_started"
);

// Widget CTA button-press events. Cleanup target: fold into `cta_clicked`
// with a `value` discriminator (e.g. value="dismissable_cta").
export const dismissableCtaButtonPressed = define(
  "dismissable_cta_button_pressed"
);
export const triagedCtaButtonPressed = define("triaged_cta_button_pressed");
